/**
 * Barcode generator for internal SKUs.
 *
 * Strategy: EAN-13 with the `2` prefix (reserved by GS1 for in-store /
 * private use — never conflicts with real manufacturer barcodes). The
 * sequence is atomic per company so two clerks creating items at the same
 * time can never collide. Returned codes are 13 digits with a valid check
 * digit and scan on any retail scanner.
 *
 * Example: 2 + 00000000042 + 8  →  "2000000000428"
 */

import { doc, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase';

// EAN-13 check digit. digits12 must be a 12-char string of digits.
const computeCheckDigit = (digits12) => {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const d = Number(digits12[i]) || 0;
    sum += (i % 2 === 0) ? d : d * 3; // position 1 (i=0) gets ×1, position 2 (i=1) gets ×3, ...
  }
  return (10 - (sum % 10)) % 10;
};

/**
 * Allocate the next sequential internal barcode for this company.
 * Atomic via Firestore runTransaction — safe under concurrent creations.
 */
export const generateNextBarcode = async (companyId) => {
  if (!companyId) throw new Error('companyId is required');
  const ref = doc(db, 'companies', companyId, 'documentCounters', 'barcode');
  const next = await runTransaction(db, async (txn) => {
    const snap = await txn.get(ref);
    const cur = snap.exists() ? (Number(snap.data().next) || 0) : 0;
    const n = cur + 1;
    txn.set(ref, { next: n, updatedAt: new Date().toISOString() }, { merge: true });
    return n;
  });
  const padded = String(next).padStart(11, '0');
  const data12 = '2' + padded;            // `2` = GS1 in-store prefix
  const check = computeCheckDigit(data12);
  return data12 + String(check);
};

export default { generateNextBarcode, };
